import LayerGroup from 'ol/layer/Group'
import VectorLayer from 'ol/layer/Vector'
import ImageLayer from 'ol/layer/Image'
import VectorSource from 'ol/source/Vector'
import ImageWMS from 'ol/source/ImageWMS'
import GeoJSON from 'ol/format/GeoJSON'
import Style from 'ol/style/Style'
import Stroke from 'ol/style/Stroke'
import Fill from 'ol/style/Fill'
import Text from 'ol/style/Text'
import Icon from 'ol/style/Icon'

export const WELCOME_THEMATIC_GROUP = 'Camadas Temáticas'

/** Mesmo padrão do mapa welcome legado (ícone 40×40, ponta no vértice). */
function resolveIconSrc(icone) {
    if (!icone) {
        return null
    }
    if (/^https?:\/\//i.test(icone)) {
        return icone
    }
    const path = icone.startsWith('/') ? icone : `/${icone}`
    return `${window.location.origin}${path}`
}

function buildPointStyle(layerDef) {
    const estilo = layerDef.estilo || {}
    const iconSrc = resolveIconSrc(layerDef.icone)
    if (!iconSrc) {
        return undefined
    }

    const styles = [
        new Style({
            image: new Icon({
                src: iconSrc,
                width: 40,
                height: 40,
                anchor: [0.5, 46],
                anchorXUnits: 'fraction',
                anchorYUnits: 'pixels',
                opacity: 0.9,
            }),
        }),
    ]

    if (estilo.label) {
        styles.push(
            new Style({
                text: new Text({
                    text: estilo.label,
                    scale: 1.3,
                    offsetY: -48,
                    fill: new Fill({ color: '#000000' }),
                    stroke: new Stroke({ color: '#FFFF99', width: 3.5 }),
                }),
            })
        )
    }

    return styles
}

function buildPolygonStyle(layerDef) {
    const estilo = layerDef.estilo || {}
    return new Style({
        stroke: new Stroke({
            color: estilo.stroke_color || 'blue',
            width: estilo.stroke_width || 3,
            lineDash: estilo.stroke_dash || undefined,
        }),
        fill: new Fill({
            color: estilo.fill_color || 'rgba(63, 154, 233, 0.1)',
        }),
        text: estilo.label
            ? new Text({
                text: estilo.label,
                scale: 1.3,
                fill: new Fill({ color: '#000000' }),
                stroke: new Stroke({ color: '#FFFF99', width: 3.5 }),
            })
            : undefined,
    })
}

function createVectorSource(layerDef) {
    if (layerDef.disponivel === false || !layerDef.geojson_url) {
        return new VectorSource()
    }

    return new VectorSource({
        url: layerDef.geojson_url,
        format: new GeoJSON(),
        projection: 'EPSG:4326',
    })
}

function createVectorLayer(layerDef) {
    const source = createVectorSource(layerDef)
    const layer = new VectorLayer({
        name: layerDef.nome,
        title: layerDef.nome,
        visible: Boolean(layerDef.visivel_padrao) && layerDef.disponivel !== false,
        displayInLayerSwitcher: true,
        source,
        style: layerDef.tipo === 'vector_polygon'
            ? buildPolygonStyle(layerDef)
            : buildPointStyle(layerDef),
    })

    source.on('featuresloadend', () => layer.changed())

    return layer
}

function createWmsLayer(layerDef) {
    const wms = layerDef.wms || {}
    const layerName = wms.layer || `${wms.workspace}:${wms.layer_name}`

    return new ImageLayer({
        name: layerDef.nome,
        title: layerDef.nome,
        visible: Boolean(layerDef.visivel_padrao),
        displayInLayerSwitcher: true,
        source: new ImageWMS({
            url: `${window.geoServerUrl}/geoserver/${wms.workspace}/wms`,
            params: {
                LAYERS: layerName,
                TILED: true,
            },
            ratio: 1,
            projection: 'EPSG:4326',
            serverType: 'geoserver',
        }),
    })
}

function isThematicLayer(def) {
    return def.grupo === WELCOME_THEMATIC_GROUP || def.grupo === 'Mapas Temáticos'
}

/**
 * Monta grupos de camadas do mapa welcome a partir da API /api/maps/welcome/config
 */
export function buildWelcomeMapLayers(layerDefs) {
    const thematic = []
    const operational = []
    const wms = []

    layerDefs.forEach((def) => {
        if (def.tipo === 'wms') {
            wms.push(createWmsLayer(def))
            return
        }
        if (isThematicLayer(def)) {
            thematic.push(createVectorLayer(def))
            return
        }
        operational.push(createVectorLayer(def))
    })

    const groups = []

    if (operational.length) {
        operational.forEach((layer) => groups.push(layer))
    }

    if (thematic.length) {
        groups.push(
            new LayerGroup({
                title: WELCOME_THEMATIC_GROUP,
                displayInLayerSwitcher: true,
                openInLayerSwitcher: true,
                visible: true,
                layers: thematic,
            })
        )
    }

    wms.forEach((layer) => groups.push(layer))

    return groups
}

export async function fetchWelcomeMapConfig() {
    const { data } = await window.axios.get('/api/maps/welcome/config')
    return data?.data ?? []
}
